import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, 
  Plus, 
  DollarSign, 
  Clock, 
  CheckCircle,
  Users,
  LayoutDashboard,
  LogOut,
  Menu,
  AlertCircle,
  Settings,
  Send,
  Bell,
  ExternalLink,
  Copy
} from "lucide-react";
import { toast } from "sonner";

// Sidebar Component
const Sidebar = ({ isOpen, setIsOpen }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { icon: <LayoutDashboard className="w-5 h-5" />, label: "Dashboard", path: "/dashboard" },
    { icon: <Plus className="w-5 h-5" />, label: "Create Invoice", path: "/create-invoice" },
    { icon: <FileText className="w-5 h-5" />, label: "Invoices", path: "/invoices" },
    { icon: <Users className="w-5 h-5" />, label: "Clients", path: "/clients" },
    { icon: <Settings className="w-5 h-5" />, label: "Settings", path: "/settings" },
  ];

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white border-r border-border
        transform transition-transform duration-200 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          <div className="flex items-center gap-2 p-6 border-b border-border">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Billuna
            </span>
          </div>

          <nav className="flex-1 p-4">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors
                      ${window.location.pathname === item.path 
                        ? 'bg-primary/10 text-primary' 
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="p-4 border-t border-border">
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Dashboard Layout
export const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-muted/30 flex">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-border px-4 lg:px-8 h-16 flex items-center justify-between sticky top-0 z-30">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>
          
          <div className="flex-1" />
          
          <div className="flex items-center gap-3">
            {user?.picture && (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="w-8 h-8 rounded-full"
              />
            )}
            <span className="text-sm font-medium hidden sm:block">{user?.name}</span>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

// Stats Card Component
const StatsCard = ({ title, value, icon, subtext, loading, variant = "default" }) => {
  if (loading) {
    return (
      <Card className="bg-white">
        <CardContent className="p-6">
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-8 w-32 mb-2" />
          <Skeleton className="h-3 w-20" />
        </CardContent>
      </Card>
    );
  }

  const bgColors = {
    default: "bg-primary/10 text-primary",
    warning: "bg-orange-100 text-orange-600",
    danger: "bg-red-100 text-red-600",
    success: "bg-green-100 text-green-600"
  };

  return (
    <Card className="bg-white card-hover">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${bgColors[variant]}`}>
            {icon}
          </div>
        </div>
        <p className="text-2xl font-bold text-foreground font-mono">{value}</p>
        {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
      </CardContent>
    </Card>
  );
};

// Status Badge Component
export const StatusBadge = ({ status }) => {
  const styles = {
    Paid: "bg-green-100 text-green-700 border-green-200",
    Pending: "bg-orange-100 text-orange-700 border-orange-200",
    Overdue: "bg-red-100 text-red-700 border-red-200"
  };

  return (
    <Badge variant="outline" className={`${styles[status] || styles.Pending} text-xs`}>
      {status}
    </Badge>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [processingReminders, setProcessingReminders] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const navigate = useNavigate();

  // Check for subscription success from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('subscription') === 'success') {
      toast.success("Welcome to Billuna Pro! Enjoy unlimited invoices.");
      window.history.replaceState({}, document.title, '/dashboard');
    } else if (params.get('subscription') === 'cancelled') {
      toast.info("Subscription was cancelled.");
      window.history.replaceState({}, document.title, '/dashboard');
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, invoicesRes, followUpRes] = await Promise.all([
          axios.get(`${API}/dashboard`, { withCredentials: true }),
          axios.get(`${API}/invoices`, { withCredentials: true }),
          axios.get(`${API}/dashboard/follow-up`, { withCredentials: true })
        ]);
        setStats(statsRes.data);
        setRecentInvoices(invoicesRes.data.slice(0, 5));
        setOverdueInvoices(followUpRes.data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSendReminder = async (invoiceId) => {
    setSendingReminder(invoiceId);
    try {
      await axios.post(`${API}/invoices/${invoiceId}/send-reminder`, {}, { withCredentials: true });
      toast.success("Reminder sent successfully!");
    } catch (error) {
      console.error("Error sending reminder:", error);
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    } finally {
      setSendingReminder(null);
    }
  };

  const handleProcessAllReminders = async () => {
    setProcessingReminders(true);
    try {
      const res = await axios.post(`${API}/reminders/process`, {}, { withCredentials: true });
      const total = res.data.pre_due_sent + res.data.due_today_sent + res.data.overdue_sent;
      if (total > 0) {
        toast.success(`${total} reminder(s) sent successfully!`);
      } else {
        toast.info("No reminders to send at this time");
      }
    } catch (error) {
      console.error("Error processing reminders:", error);
      toast.error(error.response?.data?.detail || "Failed to process reminders");
    } finally {
      setProcessingReminders(false);
    }
  };

  const copyShareableLink = (invoiceId) => {
    const link = `${window.location.origin}/invoice/view/${invoiceId}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard!");
  };

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const res = await axios.post(`${API}/subscription/upgrade`, {
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error(error.response?.data?.detail || "Failed to start upgrade");
      setUpgrading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Dashboard
            </h1>
            <p className="text-muted-foreground">Welcome back! Here's your invoice overview.</p>
          </div>
          <Button 
            onClick={() => navigate('/create-invoice')}
            className="bg-primary hover:bg-primary/90 btn-hover-lift"
            data-testid="create-invoice-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Invoice
          </Button>
        </div>

        {/* Subscription Status Banner */}
        {!loading && stats?.subscription_plan === "free" && (
          <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20 mb-6" data-testid="subscription-banner">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">
                      Free Plan: {stats?.invoices_this_month || 0} / {stats?.invoice_limit || 5} invoices this month
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Upgrade to Pro for unlimited invoices, email sending & automated reminders
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={handleUpgrade}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="upgrade-btn"
                >
                  Upgrade to Pro - $9/mo
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatsCard
            title="Total Invoices"
            value={stats?.total_invoices || 0}
            icon={<FileText className="w-5 h-5" />}
            loading={loading}
          />
          <StatsCard
            title="Total Amount"
            value={`$${(stats?.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
            icon={<DollarSign className="w-5 h-5" />}
            loading={loading}
          />
          <StatsCard
            title="Pending"
            value={`$${(stats?.pending_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
            icon={<Clock className="w-5 h-5" />}
            subtext={`${stats?.pending_count || 0} invoice(s)`}
            loading={loading}
            variant="warning"
          />
          <StatsCard
            title="Overdue"
            value={`$${(stats?.overdue_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
            icon={<AlertCircle className="w-5 h-5" />}
            subtext={`${stats?.overdue_count || 0} invoice(s)`}
            loading={loading}
            variant="danger"
          />
          <StatsCard
            title="Paid"
            value={`$${(stats?.paid_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
            icon={<CheckCircle className="w-5 h-5" />}
            subtext="Completed"
            loading={loading}
            variant="success"
          />
        </div>

        {/* Follow-up Alert */}
        {!loading && overdueInvoices.length > 0 && (
          <Card className="bg-red-50 border-red-200 mb-6" data-testid="follow-up-alert">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <Bell className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-red-800">
                      {overdueInvoices.length} invoice{overdueInvoices.length > 1 ? 's are' : ' is'} overdue
                    </p>
                    <p className="text-sm text-red-600">
                      Sending reminders can help recover payments faster.
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={handleProcessAllReminders}
                  disabled={processingReminders}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="send-all-reminders-btn"
                >
                  {processingReminders ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send All Reminders
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Recent Invoices */}
          <div className="lg:col-span-2">
            <Card className="bg-white">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold">Recent Invoices</CardTitle>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/invoices" data-testid="view-all-invoices-link">View All</Link>
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex justify-between items-center py-3 border-b border-border last:border-0">
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-24" />
                        </div>
                        <Skeleton className="h-6 w-20" />
                      </div>
                    ))}
                  </div>
                ) : recentInvoices.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">No invoices yet</p>
                    <Button onClick={() => navigate('/create-invoice')} data-testid="empty-create-invoice-btn">
                      Create your first invoice
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {recentInvoices.map((invoice) => (
                      <div 
                        key={invoice.invoice_id} 
                        className="flex justify-between items-center py-4 hover:bg-muted/50 -mx-4 px-4 cursor-pointer transition-colors"
                        onClick={() => navigate(`/invoices/${invoice.invoice_id}/edit`)}
                        data-testid={`invoice-row-${invoice.invoice_id}`}
                      >
                        <div>
                          <p className="font-medium text-foreground">#{invoice.invoice_number}</p>
                          <p className="text-sm text-muted-foreground">{invoice.client_name}</p>
                        </div>
                        <div className="text-right flex items-center gap-4">
                          <div>
                            <p className="font-semibold font-mono">${invoice.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                            <p className="text-xs text-muted-foreground">{invoice.invoice_date}</p>
                          </div>
                          <StatusBadge status={invoice.status} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Invoices Requiring Follow-up */}
          <div className="lg:col-span-1">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  Needs Follow-up
                </CardTitle>
                <CardDescription>Overdue invoices requiring attention</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2].map((i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                ) : overdueInvoices.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
                    <p className="text-muted-foreground text-sm">All caught up!</p>
                    <p className="text-xs text-muted-foreground">No overdue invoices</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {overdueInvoices.slice(0, 5).map((invoice) => (
                      <div 
                        key={invoice.invoice_id}
                        className="p-3 bg-red-50 rounded-lg border border-red-100"
                        data-testid={`overdue-invoice-${invoice.invoice_id}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium text-sm">#{invoice.invoice_number}</p>
                            <p className="text-xs text-muted-foreground">{invoice.client_name}</p>
                          </div>
                          <p className="font-semibold font-mono text-sm">${invoice.total.toFixed(2)}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="flex-1 h-7 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSendReminder(invoice.invoice_id);
                            }}
                            disabled={sendingReminder === invoice.invoice_id}
                            data-testid={`send-reminder-${invoice.invoice_id}`}
                          >
                            {sendingReminder === invoice.invoice_id ? (
                              <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <>
                                <Send className="w-3 h-3 mr-1" />
                                Remind
                              </>
                            )}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-7 text-xs px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyShareableLink(invoice.invoice_id);
                            }}
                            data-testid={`copy-link-${invoice.invoice_id}`}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="h-7 text-xs px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`/invoice/view/${invoice.invoice_id}`, '_blank');
                            }}
                            data-testid={`view-public-${invoice.invoice_id}`}
                          >
                            <ExternalLink className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
